/**
 * Optimistic cell updates for the discovery grid.
 *
 * A check, follow-up, reply, descope or restore takes ~2s on the server and the
 * grid reload another ~1s. Until the reload lands, the drawer would show the
 * thread updated but the level, ball-in-court and actions unchanged. These pure
 * helpers let the host apply the expected result immediately; the reload then
 * replaces it with the server's truth (docs/DISCOVERY_GRID_REDESIGN_SPEC.md §4.2).
 */
import {
  ARTIFACT_LEVEL_LABELS,
  LEVEL_LABELS,
  ownerForLevel,
  summarize,
  type CellLevel,
  type DiscoveryGrid,
  type GridCell,
} from './discoveryGrid';
import type { DescopeMark } from './discoveryScope';

export type CellChangeKind = 'checked' | 'follow-up' | 'reply' | 'answered' | 'descoped' | 'restored' | 'assigned' | 'unlocked';

export interface CellChange {
  kind: CellChangeKind;
  /** For `descoped`: the mark the server is expected to record. */
  descoped?: DescopeMark | null;
  /** For `answered`: the new answer. */
  answer?: unknown;
}

function withLevel(cell: GridCell, level: CellLevel): Partial<GridCell> {
  const labels = cell.kind === 'artifact' ? ARTIFACT_LEVEL_LABELS : LEVEL_LABELS;
  return { level, levelLabel: labels[level], owner: ownerForLevel(level, cell.blocked) };
}

/** The patch a change is expected to produce on `cell` (spec §4.2 ladder). */
export function cellPatchFor(cell: GridCell, change: CellChange): Partial<GridCell> {
  switch (change.kind) {
    case 'checked': return withLevel(cell, 4);
    case 'follow-up': return withLevel(cell, 2);
    case 'reply': return withLevel(cell, 3);
    // An edited answer is a fresh answer: unchecked again, back in the consultant's court.
    case 'answered': return { ...withLevel(cell, 3), answer: change.answer, answeredAt: new Date().toISOString() };
    case 'descoped': return { descoped: change.descoped ?? null };
    case 'restored': return { descoped: null };
    // An assignment sits on the config item (the block), not the cell: nothing to patch, the reload brings the name.
    case 'assigned': return {};
    // An unlock changes the item's status, so every cell of the block moves: the reload redraws them.
    case 'unlocked': return {};
    default: return {};
  }
}

/**
 * Returns a new grid with `patch` applied to the cell `cellId` and the summary
 * recomputed. Regions, blocks and cells not on the path are reused, so a
 * memoised block elsewhere does not re-render. Returns the same object when
 * the cell is not found.
 */
export function patchGridCell<T extends DiscoveryGrid>(grid: T, cellId: string, patch: Partial<GridCell>): T {
  let found = false;
  const regions = grid.regions.map((region) => {
    if (found) return region;
    const idx = region.blocks.findIndex((b) => b.cells.some((c) => c.id === cellId));
    if (idx === -1) return region;
    found = true;
    const block = region.blocks[idx];
    const cells = block.cells.map((c) => (c.id === cellId ? { ...c, ...patch } : c));
    const blocks = region.blocks.slice();
    blocks[idx] = { ...block, cells };
    return { ...region, blocks };
  });
  if (!found) return grid;
  return { ...grid, regions, summary: summarize(regions) };
}
